import { JSX, useMemo } from "react";

import { Theme, useTheme } from "@mui/material";
import { type PathOptions, type LatLngExpression } from "leaflet";
import { CircleMarker, Pane } from "react-leaflet";
import { type IntervalTab } from "src/components/IntervalTabs/enum/IntervalTab";
import { getDateRangeText } from "src/lib/date.helper";
import { filterAndSortMeasuresSummarizedDto } from "src/lib/number.helpers";
import type LocationType from "src/types/LocationType";

import { countRadius, getChangeColor } from "./MeasureLayer.helper";
import { type MeasuresSummarizedItemDto, type MeasuresSummarizedDto } from "../../../../types/dashboardDatasetDto";
import SummarizedItemPopup from "../../Popups/SummarizedItemPopup";

const styles = {
  measurePane: { zIndex: 30 },
};

function getCenterFromDataItem(item: MeasuresSummarizedItemDto): LatLngExpression {
  return [item.lat ?? 0, item.lon ?? 0];
}

function getPathOptions(item: MeasuresSummarizedItemDto, theme: Theme): PathOptions {
  return { color: getChangeColor(theme, item.isIncreased) };
}

export interface MeasureLayerProps {
  measuresData: MeasuresSummarizedDto;
  selectedTab: IntervalTab;
  displayedType: LocationType;
}

/**
 * This Layer contains Measures Data
 * Measure Data contains Polygon GeoJson Data and Coordinate Counts Data
 */
function MeasureLayer({ measuresData, selectedTab, displayedType }: MeasureLayerProps): JSX.Element {
  const theme = useTheme();
  const filteredMeasuresData = useMemo(
    () => filterAndSortMeasuresSummarizedDto(measuresData, (item) => !!(item.type === displayedType && item.lat && item.lon)),
    [measuresData, displayedType]
  );
  const filteredMeasureData = useMemo(() => filteredMeasuresData?.data ?? [], [filteredMeasuresData]);
  const allCurrentValues = useMemo(() => filteredMeasureData.map((item) => item.currentValue ?? 0), [filteredMeasureData]);
  const dateRangeText: string = useMemo(() => getDateRangeText(measuresData, selectedTab), [measuresData, selectedTab]);

  return (
    <Pane name='measure-pane' style={styles.measurePane}>
      {filteredMeasureData.map((item) => (
        <CircleMarker
          key={item.locationId}
          center={getCenterFromDataItem(item)}
          radius={countRadius(allCurrentValues, item.currentValue ?? 0)}
          fillOpacity={0.3}
          pathOptions={getPathOptions(item, theme)}
        >
          <SummarizedItemPopup item={item} dateRangeText={dateRangeText} />
        </CircleMarker>
      ))}
    </Pane>
  );
}

export default MeasureLayer;
